import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appeler } from '../api/client.ts';
import { useSession } from '../composants/session.tsx';
import { Entete } from '../composants/mise-en-page.tsx';
import {
  Carte,
  Chargement,
  DateHeure,
  Erreur,
  Etiquette,
  Vide,
  formaterDateHeure,
} from '../composants/communs.tsx';
import { BoutonImprimer, exporterTableauPdf } from '../composants/ui/index.ts';
import { useApercuPdf } from '../composants/apercu-pdf.tsx';

/**
 * Anomalies — écran de supervision.
 *
 * Traiter une anomalie EXIGE un commentaire d'au moins dix caractères, y
 * compris pour l'ignorer. Sans cette contrainte, l'écran se viderait sans
 * que rien ne soit compris — et c'est précisément l'écran qui doit rendre
 * les écarts inconfortables.
 */

interface Anomalie {
  id: string;
  type: string;
  document_type: string | null;
  detail: unknown;
  statut: 'OUVERTE' | 'TRAITEE' | 'IGNOREE';
  commentaire: string | null;
  boutique: string;
  cree_le: string;
}

const STATUTS = ['OUVERTE', 'TRAITEE', 'IGNOREE'] as const;

function lisible(type: string): string {
  const texte = type.replace(/_/g, ' ').toLowerCase();
  return texte.charAt(0).toUpperCase() + texte.slice(1);
}

export function Anomalies(): ReactNode {
  const { ouvrir } = useApercuPdf();
  const { boutiqueId } = useSession();
  const client = useQueryClient();

  // En vue consolidee, chaque ligne doit dire de quelle boutique elle
  // vient : sans cette colonne, la liste melange sans le dire.
  const consolide = boutiqueId === null;

  const [statut, setStatut] = useState<(typeof STATUTS)[number]>('OUVERTE');
  const [traitee, setTraitee] = useState<Anomalie | null>(null);

  const { data, isPending, error } = useQuery({
    queryKey: ['anomalies', boutiqueId, statut],
    queryFn: () =>
      appeler<{ donnees: Anomalie[] }>(
        `/anomalies?statut=${statut}` +
          (boutiqueId === null ? '' : `&boutique_id=${boutiqueId}`),
      ),
    select: (reponse) => reponse.donnees,
  });

  return (
    <>
      <Entete
        titre="Anomalies"
        sous="Écarts constatés par le système. Chaque traitement est journalisé."
        actions={
          <div className="champ" style={{ margin: 0, minWidth: 170 }}>
            <label htmlFor="statut">Statut</label>
            <select
              id="statut"
              value={statut}
              onChange={(evenement) => {
                setStatut(evenement.target.value as (typeof STATUTS)[number]);
              }}
            >
              {STATUTS.map((valeur) => (
                <option key={valeur} value={valeur}>
                  {lisible(valeur)}
                </option>
              ))}
            </select>
          </div>
        }
      />

      <Erreur erreur={error} />

      <div className="pile">
        {traitee !== null && (
          <Traitement
            anomalie={traitee}
            onFerme={() => {
              setTraitee(null);
            }}
            onTraitee={() => {
              setTraitee(null);
              void client.invalidateQueries({ queryKey: ['anomalies'] });
            }}
          />
        )}

        <Carte>
          {isPending ? (
            <Chargement />
          ) : data === undefined || data.length === 0 ? (
            <Vide texte="Aucune anomalie dans ce statut." />
          ) : (
            <>
              <div className="ligne" style={{ justifyContent: 'flex-end', marginBottom: 8 }}>
                <BoutonImprimer
                  onClick={() => {
                    ouvrir(() =>
                      exporterTableauPdf({
                        titre: 'Anomalies',
                        sousTitre: `Statut : ${lisible(statut)}`,
                        nomFichier: 'anomalies',
                        colonnes: [
                          { cle: 'type', entete: 'Type', rendu: () => null, texte: (a: Anomalie) => lisible(a.type) },
                          ...(consolide
                            ? [{ cle: 'boutique', entete: 'Boutique', rendu: () => null, texte: (a: Anomalie) => a.boutique }]
                            : []),
                          {
                            cle: 'cree_le',
                            entete: 'Constatée le',
                            rendu: () => null,
                            texte: (a: Anomalie) => formaterDateHeure(a.cree_le),
                          },
                          {
                            cle: 'document',
                            entete: 'Document',
                            rendu: () => null,
                            texte: (a: Anomalie) => a.document_type ?? '—',
                          },
                          {
                            cle: 'commentaire',
                            entete: 'Commentaire',
                            rendu: () => null,
                            texte: (a: Anomalie) => a.commentaire ?? '—',
                          },
                        ],
                        lignes: data,
                      }),
                    );
                  }}
                />
              </div>
              <div className="tableau-defilant">
              <table>
                <thead>
                  <tr>
                    <th>Type</th>
                    {consolide && <th>Boutique</th>}
                    <th>Constatée le</th>
                    <th>Document</th>
                    <th>Commentaire</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.map((anomalie) => (
                    <tr key={anomalie.id}>
                      <td>
                        <Etiquette ton="ambre" texte={lisible(anomalie.type)} />
                      </td>
                      {consolide && (
                        <td className="mono">{anomalie.boutique}</td>
                      )}
                      <td>
                        <DateHeure valeur={anomalie.cree_le} />
                      </td>
                      <td className="mono">{anomalie.document_type ?? '—'}</td>
                      <td>{anomalie.commentaire ?? '—'}</td>
                      <td>
                        {anomalie.statut === 'OUVERTE' && (
                          <button
                            type="button"
                            className="bouton--discret"
                            onClick={() => {
                              setTraitee(anomalie);
                            }}
                          >
                            Traiter
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          )}
        </Carte>
      </div>
    </>
  );
}

function Traitement({
  anomalie,
  onFerme,
  onTraitee,
}: {
  anomalie: Anomalie;
  onFerme: () => void;
  onTraitee: () => void;
}): ReactNode {
  const [commentaire, setCommentaire] = useState('');
  const [decision, setDecision] = useState<'TRAITEE' | 'IGNOREE'>('TRAITEE');

  const mutation = useMutation({
    mutationFn: () =>
      appeler(`/anomalies/${anomalie.id}/traitement`, {
        methode: 'POST',
        corps: { statut: decision, commentaire: commentaire.trim() },
      }),
    onSuccess: onTraitee,
  });

  const tropCourt = commentaire.trim().length < 10;

  return (
    <Carte
      titre={`Traiter — ${lisible(anomalie.type)}`}
      actions={
        <button type="button" className="bouton--discret" onClick={onFerme}>
          Fermer
        </button>
      }
    >
      <form
        onSubmit={(evenement) => {
          evenement.preventDefault();
          mutation.mutate();
        }}
      >
        <div className="champ">
          <label htmlFor="decision">Décision</label>
          <select
            id="decision"
            value={decision}
            onChange={(evenement) => {
              setDecision(evenement.target.value as 'TRAITEE' | 'IGNOREE');
            }}
          >
            <option value="TRAITEE">Traitée — l’écart a été corrigé</option>
            <option value="IGNOREE">Ignorée — sans conséquence</option>
          </select>
        </div>

        <div className="champ">
          <label htmlFor="commentaire">
            Explication (10 caractères au minimum)
          </label>
          <textarea
            id="commentaire"
            name="commentaire"
            rows={3}
            required
            value={commentaire}
            onChange={(evenement) => {
              setCommentaire(evenement.target.value);
            }}
          />
        </div>

        <Erreur erreur={mutation.error} />

        <button type="submit" disabled={tropCourt || mutation.isPending}>
          {mutation.isPending ? 'Enregistrement…' : 'Enregistrer le traitement'}
        </button>
      </form>
    </Carte>
  );
}
